/**
 * RoomSharingRules (PPL-31 → redesigned PPL-66 / C6) — the two-column consent
 * contract.
 *
 * Per the C6 canvas: this is a CONTRACT surface, not a feed. Drops cards and
 * goes typographic — rows are separated by hairlines, the two-column header
 * makes the both-sidedness structural, the derived clause reads as a contract
 * paragraph. Per-side aurora (warm inviter / cool invitee) ties it to the
 * prep room they came from.
 *
 * Behaviour preserved end-to-end from the original implementation:
 *  - subscribeToRoom feeds the doc + the other side's flags live.
 *  - Local staging — flipping a toggle updates local state; the explicit
 *    "next step" button is the only write call.
 *  - updateRoomConsent server contract unchanged (the invitee MUST NOT send
 *    refHistory; the callable rejects it).
 *  - Other side's value renders read-only (YES / NO / n/a) and updates live.
 *  - After both sides sign, the server transitions prep → joint; we read
 *    the new state from the subscription snapshot and route to /opener.
 */

import React, { CSSProperties, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { subscribeToRoom, updateRoomConsent } from '../services/roomService';
import type { ConsentFlags, Room } from '../types/room';
import SEO from '../components/SEO';

// ─── Side palette ────────────────────────────────────────────

type SideKey = 'inviter' | 'invitee';

interface SidePalette {
  /** Two aurora hue stops (a → b). Warm for inviter, cool for invitee. */
  aurora: { a: string; b: string };
  /** Primary deep colour (toggle on-state, divider). */
  deep: string;
  /** Primary soft colour (column header label, accent text). */
  soft: string;
}

const PALETTES: Record<SideKey, SidePalette> = {
  inviter: { aurora: { a: '#fb923c', b: '#d946ef' }, deep: '#d946ef', soft: '#f0abfc' },
  invitee: { aurora: { a: '#22d3ee', b: '#6366f1' }, deep: '#818cf8', soft: '#a5b4fc' },
};

// ─── Row spec ────────────────────────────────────────────────

interface Row {
  key: 'refPrep' | 'refHistory' | 'refPatterns';
  label: string;
  sub: (side: SideKey) => string;
  inviterOnly?: boolean;
}

// PPL-151: refHistory/refPatterns rows are hidden until their extractors exist —
// today only prep has a runtime data source, so offering the other toggles
// promises sharing that never happens (server consent gating for them stays
// live; unset flags mean not-consented). Restore the rows when the
// relationship-history / patterns extractors land.
const ROWS: Row[] = [
  {
    key: 'refPrep',
    label: 'reference my solo prep',
    sub: () =>
      'f8 may paraphrase what i said in prep, per item, with my permission.',
  },
];

// ─── Local flags shape ───────────────────────────────────────

interface LocalFlags {
  refPrep: boolean;
  refHistory: boolean | null; // null when the caller is the invitee
  refPatterns: boolean;
}

function pickFlags(consent: Record<string, ConsentFlags> | undefined, uid: string): ConsentFlags {
  const stored = consent?.[uid];
  return {
    refPrep: !!(stored && stored.refPrep),
    refHistory: stored && stored.refHistory !== undefined ? stored.refHistory : false,
    refPatterns: !!(stored && stored.refPatterns),
    signedAt: stored?.signedAt ?? null,
  };
}

// ─── Atoms ───────────────────────────────────────────────────

function PaperAurora({ palette }: { palette: SidePalette }) {
  return (
    <div
      aria-hidden="true"
      style={{
        position: 'absolute',
        inset: 0,
        overflow: 'hidden',
        zIndex: 0,
        pointerEvents: 'none',
      }}
    >
      <div
        style={{
          position: 'absolute',
          top: '-18%',
          left: '-18%',
          width: '110%',
          height: '70%',
          borderRadius: '50%',
          background: `radial-gradient(circle, ${palette.aurora.a} 0%, transparent 65%)`,
          filter: 'blur(60px)',
          opacity: 0.55,
        }}
      />
      <div
        style={{
          position: 'absolute',
          bottom: '-28%',
          right: '-18%',
          width: '110%',
          height: '70%',
          borderRadius: '50%',
          background: `radial-gradient(circle, ${palette.aurora.b} 0%, transparent 65%)`,
          filter: 'blur(70px)',
          opacity: 0.5,
        }}
      />
    </div>
  );
}

function Toggle({
  on,
  color,
  onClick,
  disabled,
  ariaLabel,
}: {
  on: boolean;
  color: string;
  onClick?: () => void;
  disabled?: boolean;
  ariaLabel?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={on}
      aria-label={ariaLabel}
      disabled={disabled || !onClick}
      style={{
        appearance: 'none',
        border: 0,
        cursor: disabled || !onClick ? 'not-allowed' : 'pointer',
        width: 42,
        height: 24,
        borderRadius: 999,
        background: on ? color : 'rgba(255,255,255,0.08)',
        boxShadow: on ? `0 0 10px ${color}66` : 'inset 0 0 0 1px rgba(255,255,255,0.08)',
        position: 'relative',
        flexShrink: 0,
        transition: 'background 0.15s, box-shadow 0.15s',
        padding: 0,
        opacity: disabled ? 0.5 : 1,
      }}
    >
      <span
        style={{
          position: 'absolute',
          top: 3,
          left: on ? 21 : 3,
          width: 18,
          height: 18,
          borderRadius: '50%',
          background: '#fff',
          transition: 'left 0.18s cubic-bezier(.3,.7,.4,1)',
        }}
      />
    </button>
  );
}

function StateChip({
  value,
  color,
}: {
  /** boolean for a known answer, null for "not applicable". */
  value: boolean | null;
  color: string;
}) {
  if (value === null || value === undefined) {
    return (
      <div
        style={{
          width: 50,
          height: 24,
          borderRadius: 999,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 10,
          fontWeight: 600,
          color: 'rgba(232,220,250,0.35)',
          background: 'rgba(255,255,255,0.025)',
          border: '1px dashed rgba(255,255,255,0.1)',
        }}
      >
        n/a
      </div>
    );
  }
  return (
    <div
      style={{
        width: 50,
        height: 24,
        borderRadius: 999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: '0.05em',
        color: value ? '#fff' : 'rgba(232,220,250,0.55)',
        background: value ? color + '33' : 'rgba(255,255,255,0.04)',
        border: value ? `1px solid ${color}66` : '1px solid rgba(255,255,255,0.08)',
      }}
    >
      {value ? 'YES' : 'NO'}
    </div>
  );
}

// ─── Page ────────────────────────────────────────────────────

const RoomSharingRules: React.FC = () => {
  const { mediationId } = useParams<{ mediationId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const uid = user?.uid ?? null;

  const [mediation, setRoom] = useState<Room | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  // Hydrate from the first snapshot so live updates don't stomp local edits.
  const hydratedRef = useRef(false);
  const [myFlags, setMyFlags] = useState<LocalFlags>({
    refPrep: false,
    refHistory: false,
    refPatterns: false,
  });

  // ── Subscribe ──────────────────────────────────────────────
  useEffect(() => {
    if (!mediationId) {
      setError('missing mediation id');
      setLoading(false);
      return undefined;
    }
    const unsub = subscribeToRoom(
      mediationId,
      (data) => {
        if (!data) {
          setRoom(null);
          setError('room not found');
          setLoading(false);
          return;
        }
        setRoom(data);
        setError(null);
        setLoading(false);

        if (!hydratedRef.current && uid) {
          const f = pickFlags(data.consent, uid);
          setMyFlags({
            refPrep: !!f.refPrep,
            refHistory: f.refHistory === null ? null : !!f.refHistory,
            refPatterns: !!f.refPatterns,
          });
          hydratedRef.current = true;
        }
      },
      (err) => {
        setError(err?.message ?? 'could not load room');
        setLoading(false);
      },
    );
    return () => {
      try {
        unsub();
      } catch {
        /* best-effort */
      }
    };
  }, [mediationId, uid]);

  // ── Derived ────────────────────────────────────────────────
  const isInviter = !!mediation && !!uid && mediation.inviterUid === uid;
  const sideKey: SideKey = isInviter ? 'inviter' : 'invitee';
  const palette = PALETTES[sideKey];
  const otherSideKey: SideKey = sideKey === 'inviter' ? 'invitee' : 'inviter';
  const otherPalette = PALETTES[otherSideKey];

  const otherUid = useMemo(() => {
    if (!mediation || !uid) return null;
    return mediation.inviterUid === uid ? mediation.inviteeUid : mediation.inviterUid;
  }, [mediation, uid]);

  const theirFlags = useMemo<ConsentFlags | null>(() => {
    if (!mediation || !otherUid) return null;
    return pickFlags(mediation.consent, otherUid);
  }, [mediation, otherUid]);

  const mySigned = !!(mediation && uid && mediation.consent?.[uid]?.signedAt);
  const theirSigned = !!theirFlags?.signedAt;

  // ── Handlers ───────────────────────────────────────────────
  const setFlag = useCallback(
    (key: Row['key'], val: boolean) => {
      if (key === 'refHistory' && !isInviter) return;
      setMyFlags((prev) => ({ ...prev, [key]: val }));
    },
    [isInviter],
  );

  const handleSave = useCallback(async () => {
    if (!mediationId || !uid || saving) return;
    setSaving(true);
    setSaved(false);
    setError(null);
    try {
      const payload: {
        mediationId: string;
        refPrep: boolean;
        refHistory?: boolean;
        refPatterns: boolean;
      } = {
        mediationId,
        refPrep: !!myFlags.refPrep,
        refPatterns: !!myFlags.refPatterns,
      };
      if (isInviter) payload.refHistory = !!myFlags.refHistory;
      const res = await updateRoomConsent(payload);
      setSaved(true);
      // After confirming, always move the user forward. If BOTH sides have now
      // confirmed (`bothSigned`) the last person skips the waiting room and goes
      // straight into the joint chat (the opener handles the brief synthesis
      // wait). Otherwise this is the first side — send them to the waiting room,
      // which auto-promotes them into the joint chat when the other confirms.
      // The brief "saved" flash plays first.
      window.setTimeout(() => {
        setSaved(false);
        if (!mediationId) return;
        navigate(
          res?.bothSigned
            ? `/rooms/${mediationId}/opener`
            : `/rooms/${mediationId}/waiting`,
          { replace: true },
        );
      }, 2200);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'could not save sharing rules';
      setError(msg);
    } finally {
      setSaving(false);
    }
  }, [mediationId, uid, saving, myFlags, isInviter, navigate]);

  // ── Loading / error short-circuits ─────────────────────────
  if (loading) {
    return (
      <div style={{ height: '100%', minHeight: 0, background: '#0a0510', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <SEO title="sharing rules" />
        <div style={{ color: 'rgba(232,220,250,0.65)' }}>loading…</div>
      </div>
    );
  }
  if (error && !mediation) {
    return (
      <div style={{ height: '100%', minHeight: 0, background: '#0a0510', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <SEO title="sharing rules" />
        <div style={{ color: 'rgba(232,220,250,0.65)' }}>{error}</div>
      </div>
    );
  }

  // ── Render ─────────────────────────────────────────────────
  const containerStyle: CSSProperties = {
    position: 'relative',
    // Fill the shell's <main> (which sits below the top-bar chrome) rather than
    // forcing a full *viewport* height — otherwise the page is taller than its
    // slot and <main> scrolls, dropping the footer button below the fold. The
    // top bar / footer stay fixed and the contract surface scrolls internally.
    height: '100%',
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    background: '#0a0510',
    color: '#fff',
    fontFamily: "'Inter', system-ui, sans-serif",
    overflow: 'hidden',
  };

  // Therefore-clause built fresh on every render so it tracks toggles live.
  const renderThereforeClause = () => {
    const anyOn =
      myFlags.refPrep || !!myFlags.refHistory || myFlags.refPatterns;
    if (!anyOn) {
      return (
        <span style={{ color: 'rgba(232,220,250,0.55)', fontStyle: 'normal' }}>
          f8 will know only what you say in the shared room, in real time.
        </span>
      );
    }
    const parts: React.ReactNode[] = [];
    if (myFlags.refPrep) {
      parts.push(<span key="prep">things you chose to surface from prep</span>);
    }
    if (myFlags.refHistory) {
      if (parts.length) parts.push(', ');
      parts.push(
        <span key="history">longer context only when you confirm in the moment</span>,
      );
    }
    if (myFlags.refPatterns) {
      if (parts.length) parts.push(', ');
      parts.push(<span key="patterns">patterns across your relationships</span>);
    }
    return (
      <>
        f8 may bring {parts}.
      </>
    );
  };

  return (
    <div style={containerStyle}>
      <SEO title="sharing rules" noindex />
      <PaperAurora palette={palette} />

      {/* top bar */}
      <div
        style={{
          position: 'relative',
          zIndex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '14px 24px 12px',
        }}
      >
        {/* No back button — forward-only flow (PPL-161). Spacer keeps the
            stage label centered. */}
        <div style={{ width: 40 }} />
        <div
          style={{
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: '0.16em',
            textTransform: 'uppercase',
            color: 'rgba(232,220,250,0.4)',
          }}
        >
          stage 03 / sharing rules
        </div>
        <div style={{ width: 40 }} />
      </div>

      {/* contract surface */}
      <div
        style={{
          position: 'relative',
          zIndex: 1,
          flex: 1,
          minHeight: 0,
          overflow: 'auto',
          padding: '14px 28px 18px',
        }}
      >
        <div style={{ maxWidth: 580, margin: '0 auto' }}>
          {/* contract title */}
          <div
            style={{
              fontSize: 9.5,
              fontWeight: 700,
              letterSpacing: '0.2em',
              textTransform: 'uppercase',
              color: 'rgba(232,220,250,0.5)',
              paddingBottom: 6,
              borderBottom: '1px solid rgba(255,255,255,0.1)',
              marginBottom: 18,
            }}
          >
            rules for the shared room · session 1
          </div>

          <h1
            style={{
              margin: '0 0 12px',
              fontSize: 26,
              fontWeight: 700,
              color: '#fff',
              letterSpacing: '-0.028em',
              lineHeight: 1.12,
            }}
          >
            what can f8 carry into the room?
          </h1>
          <p
            style={{
              margin: '0 0 30px',
              fontSize: 13.5,
              color: 'rgba(232,220,250,0.6)',
              lineHeight: 1.6,
              maxWidth: 360,
            }}
          >
            each of you decides for your own side. defaults are off. nothing crosses without consent from the side it belongs to.
          </p>

          {/* column header */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 56px 56px',
              alignItems: 'end',
              padding: '0 0 10px',
              borderBottom: '1px solid rgba(255,255,255,0.18)',
            }}
          >
            <div
              style={{
                fontSize: 9,
                fontWeight: 700,
                letterSpacing: '0.2em',
                textTransform: 'uppercase',
                color: 'rgba(232,220,250,0.4)',
              }}
            >
              the rule
            </div>
            <div style={{ textAlign: 'center' }}>
              <div
                style={{
                  fontSize: 13,
                  fontWeight: 700,
                  color: palette.soft,
                  letterSpacing: '-0.01em',
                }}
              >
                you
              </div>
              <div
                style={{
                  fontSize: 9,
                  color: 'rgba(232,220,250,0.4)',
                  letterSpacing: '0.1em',
                  marginTop: 1,
                }}
              >
                {mySigned ? 'confirmed' : ''}
              </div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div
                style={{
                  fontSize: 13,
                  fontWeight: 700,
                  color: otherPalette.soft,
                  letterSpacing: '-0.01em',
                }}
              >
                them
              </div>
              <div
                style={{
                  fontSize: 9,
                  color: 'rgba(232,220,250,0.4)',
                  letterSpacing: '0.1em',
                  marginTop: 1,
                }}
              >
                {theirSigned ? 'confirmed' : ''}
              </div>
            </div>
          </div>

          {/* rule rows */}
          {ROWS.map((row) => {
            const myIsNa = row.inviterOnly && !isInviter;
            const theirIsNa = !!row.inviterOnly && !isInviter ? false : !!row.inviterOnly && !!otherUid && !!mediation && mediation.inviteeUid === otherUid;
            // For the "them" cell:
            //  - row is inviter-only and `them` is the invitee → n/a (null)
            //  - they haven't signed yet → undecided (null), never a hard "no"
            //  - they signed → boolean from their flags
            const theirValue: boolean | null = theirIsNa
              ? null
              : theirSigned
                ? !!theirFlags?.[row.key]
                : null;
            const myValue = myFlags[row.key];
            return (
              <div
                key={row.key}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 56px 56px',
                  alignItems: 'center',
                  padding: '18px 0',
                  borderBottom: '1px solid rgba(255,255,255,0.08)',
                  opacity: myIsNa ? 0.55 : 1,
                }}
              >
                <div style={{ paddingRight: 12 }}>
                  <div
                    style={{
                      fontSize: 14,
                      fontWeight: 600,
                      color: '#fff',
                      letterSpacing: '-0.005em',
                    }}
                  >
                    {row.label}
                  </div>
                  <div
                    style={{
                      fontSize: 11.5,
                      color: 'rgba(232,220,250,0.5)',
                      lineHeight: 1.5,
                      marginTop: 4,
                    }}
                  >
                    {row.sub(sideKey)}
                  </div>
                </div>
                <div style={{ display: 'flex', justifyContent: 'center' }}>
                  {myIsNa ? (
                    <StateChip value={null} color={palette.deep} />
                  ) : (
                    <Toggle
                      on={!!myValue}
                      color={palette.deep}
                      onClick={() => setFlag(row.key, !myValue)}
                      disabled={saving}
                      ariaLabel={row.label}
                    />
                  )}
                </div>
                <div style={{ display: 'flex', justifyContent: 'center' }}>
                  <StateChip value={theirValue} color={otherPalette.deep} />
                </div>
              </div>
            );
          })}

          {/* derived clause */}
          <div style={{ marginTop: 28 }}>
            <div
              style={{
                fontSize: 9,
                fontWeight: 700,
                letterSpacing: '0.2em',
                textTransform: 'uppercase',
                color: 'rgba(232,220,250,0.4)',
                marginBottom: 8,
              }}
            >
              therefore, in the shared room
            </div>
            <div
              style={{
                fontSize: 14,
                color: '#fff',
                lineHeight: 1.65,
                fontStyle: 'italic',
                letterSpacing: '-0.005em',
                paddingLeft: 12,
                borderLeft: `2px solid ${palette.deep}66`,
              }}
            >
              {renderThereforeClause()}
            </div>
          </div>

          {/* pace mark */}
          <div
            style={{
              marginTop: 20,
              textAlign: 'center',
              fontSize: 11.5,
              color: 'rgba(232,220,250,0.5)',
              letterSpacing: '0.02em',
            }}
          >
            you can change any of these between sessions.
          </div>

          {error && (
            <div
              role="alert"
              style={{
                marginTop: 18,
                padding: '10px 14px',
                borderRadius: 12,
                background: 'rgba(239,68,68,0.08)',
                border: '1px solid rgba(239,68,68,0.25)',
                fontSize: 13,
                color: '#fca5a5',
              }}
            >
              {error}
            </div>
          )}

          {/* PPL-44 F2 — calm waiting state after I've signed but they haven't */}
          {mySigned && !theirSigned && mediation?.state !== 'joint' && (
            <div
              role="note"
              style={{
                marginTop: 18,
                padding: '12px 14px',
                borderRadius: 12,
                background: 'rgba(255,255,255,0.04)',
                border: '1px solid rgba(255,255,255,0.08)',
                fontSize: 12.5,
                color: 'rgba(232,220,250,0.7)',
                lineHeight: 1.55,
              }}
            >
              <b style={{ color: '#fff', fontWeight: 600 }}>you&apos;ve confirmed.</b>{' '}
              waiting for the other side to confirm —{' '}
              <a
                href="/rooms"
                onClick={(e) => {
                  e.preventDefault();
                  navigate('/rooms');
                }}
                style={{ color: palette.soft, textDecoration: 'underline', fontWeight: 600 }}
              >
                go to rooms
              </a>
            </div>
          )}
        </div>
      </div>

      {/* footer / save */}
      <div
        style={{
          position: 'relative',
          zIndex: 1,
          padding: '14px 28px 24px',
          borderTop: '1px solid rgba(255,255,255,0.08)',
          background: 'rgba(10,7,13,0.5)',
          backdropFilter: 'blur(8px)',
        }}
      >
        <div style={{ maxWidth: 580, margin: '0 auto' }}>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            style={{
              appearance: 'none',
              border: 0,
              cursor: saving ? 'progress' : 'pointer',
              width: '100%',
              padding: '15px 22px',
              borderRadius: 999,
              background: 'linear-gradient(135deg, #d946ef 0%, #a855f7 100%)',
              color: '#fff',
              fontSize: 14.5,
              fontWeight: 700,
              fontFamily: 'inherit',
              boxShadow: '0 8px 24px rgba(217,70,239,0.32)',
              opacity: saving ? 0.6 : 1,
            }}
          >
            {saving
              ? 'saving…'
              : saved
                ? 'saved'
                : mySigned
                  ? 'update & continue'
                  : 'next step'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RoomSharingRules;
